import type { BlockEmbed } from 'quill/blots/block';
import React, { useEffect, useState } from 'react';
import ButtonLinkEditor from './ButtonLinkEditor';
import ToolbarButton, { ToolbarButtonGroup } from './buttons/ToolbarButton';
import ToolbarCommentButtonGroup from './buttons/ToolbarCommentButtonGroup';
import ToolbarSpanButtonGroup from './buttons/ToolbarSpanButtonGroup';
import DeleteMenuItem from './DeleteMenuItem';
import FloatingToolbar from './FloatingToolbar';
import Toolbar, { ToolbarOverflowMenu } from './Toolbar';
import { MenuItem, MenuSeparator } from '../../../components/Menu';
import triggerIcon from '../../../icons/non-prefilled/16x16/trigger.svg';
import ClickOutsideHandler from '../../../utils/ClickOutsideHandler';
import { isFeatureEnabled } from '../../../utils/features';
import useButtonAction from '../../button/useButtonAction';
import type { ButtonType } from '../../formats/ButtonBlot';
import ButtonBlot from '../../formats/ButtonBlot';
import type { HoveredBlotListenerSetting } from '../../HoveredBlotListener';
import { useHoveredBlotListener } from '../../HoveredBlotListener';
import { useQuill } from '../../QuillContext';
import replaceEmbedBlotWith from '../../utils/replaceEmbedBlotWith';
import CopyLinkMenuItem from '../CopyLinkMenuItem';
import { linkIcon } from '../editorIcons';
import OpenInNewTabMenuItem from '../OpenInNewTabMenuItem';

interface ButtonToolbarOverflowMenuItemsProps {
  blot: ButtonBlot;
}

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const ButtonToolbarOverflowMenuItems = ({
  blot,
}: ButtonToolbarOverflowMenuItemsProps) => {
  const { quill } = useQuill();
  const performButtonAction = useButtonAction();
  const { type, destination } = blot.getState();

  if (!quill) return null;

  return (
    <>
      {destination !== '' && (
        <>
          {type === 'navigation' && (
            <>
              <OpenInNewTabMenuItem link={destination} />
              <CopyLinkMenuItem link={destination}>
                Copy button link
              </CopyLinkMenuItem>
            </>
          )}

          {type === 'action' && (
            <MenuItem
              icon={triggerIcon}
              onClick={() => performButtonAction(destination)}
            >
              Trigger test action
            </MenuItem>
          )}

          <MenuSeparator />
        </>
      )}

      <DeleteMenuItem onClick={() => blot.remove()}>
        Delete button
      </DeleteMenuItem>
    </>
  );
};

type Formats = Record<string, string[] | string | null | undefined> & {
  comment?: string[] | null;
};

interface BaseButtonToolbarProps {
  hoveredBlot: ButtonBlot;
  formats: Formats;
  setPersist: (persist: boolean) => void;
}

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const BaseButtonToolbar = ({
  hoveredBlot,
  formats,
  setPersist,
}: BaseButtonToolbarProps) => {
  const { readOnly, quill } = useQuill();

  const { type, destination } = hoveredBlot.getState();
  const [editingTarget, setEditingTarget] = useState<ButtonType | null>(null);

  useEffect(() => {
    if (readOnly) {
      setPersist(false);
    }
  }, [readOnly, setPersist]);

  if (editingTarget) {
    return (
      <ClickOutsideHandler
        onClickOutside={() => {
          setPersist(false);
        }}
      >
        <ButtonLinkEditor
          blot={hoveredBlot}
          editingTarget={editingTarget}
          initialDestination={destination}
          onEditingTargetChange={(target) => {
            setEditingTarget(target);
            if (target == null) {
              setPersist(false);
            }
          }}
          actionsEnabled={isFeatureEnabled('api')}
          onSave={(nextDestination) => {
            if (!quill) return;

            replaceEmbedBlotWith(
              quill,
              hoveredBlot,
              { button: { type: editingTarget, destination: nextDestination } },
              hoveredBlot.formats(),
            );
          }}
        />
      </ClickOutsideHandler>
    );
  }

  return (
    <Toolbar
      targetBlot={hoveredBlot}
      overflowMenu={
        readOnly ? undefined : (
          <ToolbarOverflowMenu onMenuOpenChange={setPersist}>
            <ButtonToolbarOverflowMenuItems blot={hoveredBlot} />
          </ToolbarOverflowMenu>
        )
      }
    >
      {!readOnly && (
        <>
          <ToolbarButtonGroup>
            <ToolbarButton
              icon={linkIcon}
              tooltip="Edit link"
              onClick={() => {
                setPersist(true);
                setEditingTarget(type);
              }}
            />
          </ToolbarButtonGroup>

          <ToolbarSpanButtonGroup
            span={formats.span}
            onSpanChange={(newSpan) => {
              hoveredBlot.format('span', newSpan);
            }}
          />
        </>
      )}

      <ToolbarCommentButtonGroup
        blot={hoveredBlot}
        existingComment={formats.comment}
        onReactionPickerOpenChange={setPersist}
      />
    </Toolbar>
  );
};

type Context = {
  formats: Record<string, string[] | string | null | undefined>;
};

const hoveredBlotListenerSetting: HoveredBlotListenerSetting<
  Context,
  ButtonBlot
> = {
  isHoveredBlot(blot) {
    return blot instanceof ButtonBlot;
  },
  getContext(blot: BlockEmbed) {
    return {
      formats: blot.formats(),
    };
  },
};

const ButtonToolbar = () => {
  const { quill } = useQuill();

  const {
    bounds,
    hoveredBlot,
    context: { formats } = { formats: undefined },
    setPersist,
  } = useHoveredBlotListener(quill, hoveredBlotListenerSetting);

  return (
    <FloatingToolbar.Toolbar bounds={bounds}>
      {hoveredBlot && formats && (
        <BaseButtonToolbar
          hoveredBlot={hoveredBlot}
          formats={formats}
          setPersist={setPersist}
        />
      )}
    </FloatingToolbar.Toolbar>
  );
};

export default ButtonToolbar;
